// src/services/twoFactorErrors.ts — the step-up errors that more than one client
// needs. Split from twoFactorClient so the prompt broker (which the shared
// interceptor imports) can name the cancellation without pulling in the money-op
// axios instance that twoFactorClient creates at module scope.
import { isStepUpTooManyAttempts } from "./stepUp";

/** Error carrying the API's machine-readable `code` so callers can branch on it. */
export class ApiError extends Error {
  code?: string;
  status?: number;
  /** The api's structured extras, when it sent any (e.g. `{ reason: "already_used" }`). */
  details?: Record<string, unknown>;

  constructor(message: string, code?: string, status?: number) {
    super(message);
    this.name = "ApiError";
    this.code = code;
    this.status = status;
  }
}

/** Rejection used when the operator dismisses the 2FA prompt (not a real failure). */
export class TwoFactorCancelledError extends Error {
  constructor() {
    super("Two-factor verification was cancelled");
    this.name = "TwoFactorCancelledError";
  }
}

/**
 * The step-up prompt closed on a lockout or a lost answer and has already told
 * the operator so. A cancellation as far as the screen is concerned (silent, no
 * second toast); the server's answer is kept as `answer`.
 */
export class StepUpPromptReportedError extends TwoFactorCancelledError {
  readonly answer: unknown;

  constructor(answer: unknown) {
    super();
    this.name = "StepUpPromptReportedError";
    this.answer = answer;
  }
}

export const isTwoFactorCancelled = (
  error: unknown
): error is TwoFactorCancelledError => error instanceof TwoFactorCancelledError;

/**
 * The prompt closed on a lockout or a lost answer (not "Not now"). Still silent
 * for the screen, but a lost re-send may have landed, so a screen that shows the
 * affected record refreshes it instead of trusting what it already holds.
 */
export const isStepUpPromptReported = (
  error: unknown
): error is StepUpPromptReportedError =>
  error instanceof StepUpPromptReportedError;

/**
 * The prompt closed on a lost answer: the re-send may have landed, so the screen
 * must re-read what it acted on. A lockout is not one: the server refused that
 * re-send, so nothing changed and a form keeps its draft.
 */
export const isStepUpOutcomeUnknown = (
  error: unknown
): error is StepUpPromptReportedError =>
  isStepUpPromptReported(error) && !isStepUpTooManyAttempts(error.answer);

/**
 * The one step-up prompt settled this request: "Not now", or the prompt closed
 * on a lockout or a lost answer. The prompt has already said everything, so the
 * caller shows nothing more (true). Only a lost answer may have landed, so
 * `refresh` runs for that one, letting the screen re-read the record it acted on.
 */
export const isSettledByStepUpPrompt = (
  error: unknown,
  refresh?: () => void
): boolean => {
  if (!isTwoFactorCancelled(error)) return false;
  if (isStepUpOutcomeUnknown(error)) refresh?.();
  return true;
};
